import {Component, DestroyRef, DoCheck, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NlrPermissionsDisabledPipe} from '../../../../common/pipe/nlr-permissions-disabled-pipe';
import {AnnotationTool} from './annotation-tool';
import {AnnotationConstructionToolService} from './services/annotation-construction-tool.service';
import {AnnotationEditorToolService} from './services/annotation-editor-tool.service';
import {LookupService} from '../../../../common/service/lookup.service';
import {MapService} from '../../../services/map.service';
import {AngleUtilService} from '../../../services/features/angle/angle-util.service';
import {PlanModelService} from '../../../../common/service/plan-model.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {MapToolsService} from '../../../services/map-tools.service';
import {NgEventBus} from 'ng-event-bus';
import {isDefined} from '../../../../common/service/data-util';
import {NlrHasPermissionsPipe} from '../../../../common/pipe/nlr-has-permissions.pipe';
import {NlrPatternParserDirective} from '../../../../common/directive/nlr-pattern-parser.directive';
import {NlrPatternDirective} from '../../../../common/directive/nlr-pattern.directive';
import {MaxLengthInputLimitDirective} from '../../../../common/directive/max-length-input-limit.directive';
import {NlrFormFieldHasErrorPipe} from '../../../../common/pipe/nlr-form-field-has-error.pipe';
import {NlrAnglePatternDirective} from '../../../../common/directive/nlr-angle-pattern.directive';
import {NlrUppercaseDirective} from '../../../../common/directive/nlr-uppercase.directive';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgSelectModule} from '@ng-select/ng-select';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
@Component({
    selector: 'nlr-annotation-tool',
    imports: [
        CommonModule,
        FormsModule,
        NlrPermissionsDisabledPipe,
        NlrHasPermissionsPipe,
        NlrPatternParserDirective,
        NlrPatternDirective,
        MaxLengthInputLimitDirective,
        NlrFormFieldHasErrorPipe,
        NlrAnglePatternDirective,
        NlrUppercaseDirective,
        NgSelectModule
    ],
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}],
    providers: [CommonModule],
    templateUrl: './annotation-tool.component.html',
    styleUrl: './annotation-tool.component.scss'
})
export class AnnotationToolComponent implements OnInit, OnDestroy, DoCheck {
    @Input()
    tool: AnnotationTool;
    submitted: boolean = false;
    @Input()
    active: boolean;
    displayConfig: string[];
    setFormPristineOnDeactivation: boolean;
    createActiveFormInvalid: boolean;
    shortFormat: boolean;
    private previousState: string;
    constructor(
        private destroyRef: DestroyRef,
        private lookupService: LookupService,
        private mapService: MapService,
        private angleUtilService: AngleUtilService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private mapToolsService: MapToolsService,
        private eventBus: NgEventBus,
        private annotationConstructionToolService: AnnotationConstructionToolService,
        private annotationEditorToolService: AnnotationEditorToolService
    ) {}
    @Input()
    parentForm: NgForm;
    title = '';
    pristineAttributes = [];
    attributes = [];
    annotationTypes = [];
    selectedAnnotationType = {
        id: undefined,
        description: undefined
    };
    selectedFeatures = undefined;
    compareEditableOrderToolsWidget = (a: any, b: any) => a.displayOrder - b.displayOrder;

    ngOnInit(): void {
        this.title = `AnnotationToolComponent for ${this.tool.type}`;

        console.info(`${this.title} - ngOnInit`);

        this.eventBus
            .on(`${this.tool.type}ReloadAnnotation`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => {
                console.debug(`${this.title} - on - ${this.tool.type}ReloadAnnotation`);
                const data: any = meta.data;
                this.selectedFeatures = this.spatialUtilService
                    .getAnnotations()
                    .filter(f => f.attributes.id === data.attributes.id && f.attributes.featuresubtype === data.attributes.featuresubtype);
                this.getSelectedAnnotationType(data.attributes, () => {
                    this.loadAnnotationTypeLookup(this.selectedAnnotationType, () => {
                        this.updateToolServices();
                    });
                });
            });

        this.eventBus
            .on(`${this.tool.type}ClearAnnotationTool`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - ${this.tool.type}ClearAnnotationTool`);
                this.clearFeatureAttributes();
            });

        this.eventBus
            .on(`${this.tool.type}CreateAnnotationTool`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - ${this.tool.type}CreateAnnotationTool`);
                this.getForm().control.markAllAsTouched();
                this.validate();
            });

        this.eventBus
            .on('updateErrors')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(args => {
                const data: any = args.data;
                console.debug(`${this.title} - on - updateErrors`);
                this.tool.errors = [];
                this.tool.errors.push(data);
            });

        this.eventBus
            .on('resetSelectedFeatures')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - resetSelectedFeatures`);
                this.selectedFeatures = undefined;
            });

        this.eventBus
            .on('clearError')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - clearError`);
                this.tool.errors = [];
            });

        this.eventBus.cast('resizeAnnotationWidget');
        this.init();
    }

    init() {
        console.debug(this.title + ' : init');
        this.setupFeatureAttributes(() => {
            this.updateToolServices();
        });
    }
    getFormName() {
        return `${this.tool.type}AnnotationToolForm`;
    }

    getForm() {
        return this.parentForm;
    }

    onFocus() {
        this.shortFormat = true;
    }

    onBlur() {
        this.shortFormat = false;
    }

    hasMinOptionalError() {
        const form = this.getForm();

        if (!form || (this.tool.type === 'edit' && !this.selectedFeatures)) {
            return false;
        }

        const attrsList = this.attributes.filter(a => a.prop.includes('attribute') && !this.isDisabled(a.prop));
        if (attrsList.some(a => a.mandatory)) {
            return false;
        }
        return attrsList.length > 1 && !attrsList.some(a => a.value && a.value !== 'null');
    }

    showAttribute() {
        return this.selectedFeatures || this.tool.type !== 'edit';
    }

    showErrors() {
        const form = this.getForm();
        return form && this.tool.errors && this.tool.errors.length && (this.tool.type === 'edit' || form.dirty || form.submitted);
    }

    validate() {
        console.debug(`${this.title} - validate`);

        this.tool.errors = [];

        const form = this.getForm();
        form.onSubmit(undefined);

        if (form.valid) {
            const values = this.getAttributes();
            if (this.tool.type === 'edit') {
                this.annotationEditorToolService.create(values.attributes, this.selectedFeatures);
            }
            if (this.tool.type === 'construct') {
                this.annotationConstructionToolService.create(() => this.getAttributes());
            }
        } else {
            this.eventBus.cast('resetActionBehaviours');
        }
    }

    getSelectedAnnotationType(featureAttributes, callback) {
        console.debug(this.title + ' : getSelectedAnnotationType');

        if (isDefined(featureAttributes.featuresubtype)) {
            this.annotationTypes.forEach(annotationType => {
                if (annotationType.id === featureAttributes.featuresubtype) {
                    this.selectedAnnotationType = annotationType;

                    if (callback) {
                        callback();
                    }
                }
            });
        }
    }

    ngDoCheck(): void {
        this.doCheck();

        if (!this.selectedFeatures || this.selectedFeatures.length === 0 || !this.mapToolsService.isActive('ANNOTATION_TOOLS_EDIT')) {
            this.previousState = '';
            return;
        }

        const selectedFeatureIds = this.selectedFeatures.map(f => f.attributes.id);
        const annotationMap = this.spatialUtilService.getFeatureMap(
            this.spatialUtilService.getAnnotations().filter(f => selectedFeatureIds.includes(f.attributes.id))
        );

        const currentState = this.selectedFeatures
            .filter(f => {
                const annotation = annotationMap[f.attributes.id];
                return !annotation || annotation.geometry.x !== f.geometry.x || annotation.geometry.y !== f.geometry.y;
            })
            .map(f => f.attributes.id)
            .sort((a, b) => a - b)
            .join();

        if (this.previousState !== currentState) {
            this.previousState = currentState;

            if (currentState) {
                this.selectedFeatures = this.spatialUtilService.getAnnotations().filter(f => selectedFeatureIds.includes(f.attributes.id));
            }
        }
    }

    private setFormPristine() {
        if (!this.getForm()) {
            return;
        }

        setTimeout(() => {
            const form = this.getForm();
            if (form.submitted || form.dirty) {
                // Reset form to reset submitted status
                form.resetForm(form.value);
            }
            // Set required fields back to pristine state
            this.attributes
                .map(a => form.controls[`${this.tool.type}${a.prop}`])
                .filter(control => control && control.dirty && control.invalid && control.errors && control.errors['required'])
                .forEach(control => {
                    control.reset();
                    console.log('field', control);
                });
        });
    }

    changeSelectedAnnotationType(annotationType: any) {
        console.debug(this.title + ' : changeSelectedAnnotationType');
        this.selectedAnnotationType = annotationType;
        this.loadAnnotationTypeLookup(this.selectedAnnotationType, () => {
            this.updateToolServices();
        });

        if (this.mapToolsService.isInactive('ANNOTATION_TOOLS_CREATE') && this.mapToolsService.isInactive('ANNOTATION_TOOLS_EDIT')) {
            this.setFormPristine();
        }
    }
    loadAnnotationTypeLookup(annotationTypeSelected, callback) {
        console.debug(this.title + ' : loadAnnotationTypeLookup');

        this.attributes = [];

        this.lookupService.getLookups('annotationAttribute').forEach(attribute => {
            if (isDefined(annotationTypeSelected) && attribute.annotationTypeId === annotationTypeSelected.id) {
                if (attribute.attributeType === 'DOMAIN' && attribute.lookupService !== null) {
                    attribute.values = this.lookupService.getLookups(attribute.lookupService);

                    if (attribute.lookupDefaultId !== null) {
                        attribute.values.forEach(lookup => {
                            if (String(lookup.id).toString() === attribute.lookupDefaultId) {
                                attribute.value = lookup;
                            }
                        });
                    }
                } else if (attribute.attributeType.indexOf('RADIO') !== -1) {
                    attribute.value = attribute.lookupDefaultId;
                    attribute.values = [
                        {value: 'YES', description: 'Yes'},
                        {value: 'NO', description: 'No'}
                    ];
                } else if (attribute.attributeType.indexOf('STRING') !== -1) {
                    attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);
                    if (attribute.pattern) {
                        if (attribute.annotationTypeId === 24 && attribute.prop === 'attribute3') {
                            attribute.pattern = /^(\d{1,6}|\d{1,6}[-]\d{1,6})(,\s*(\d{1,6}|\d{1,6}[-]\d{1,6}))*$/;
                        } else {
                            attribute.pattern = new RegExp(attribute.pattern);
                        }
                    }
                    attribute.value = attribute.lookupDefaultId;
                } else if (attribute.attributeType.indexOf('DECIMAL') !== -1) {
                    const decimalLength = this.getAttributeTypeLength(attribute.attributeType);
                    attribute.pattern = new RegExp(`^-?\\d{1,15}(\\.\\d{1,${decimalLength}})?$`);
                    if (attribute.lookupDefaultId) {
                        attribute.value = Number(attribute.lookupDefaultId);
                    }
                } else if (attribute.attributeType.indexOf('INTEGER') !== -1) {
                    attribute.value = Number(attribute.lookupDefaultId);
                    attribute.attributeTypeLength = this.getAttributeTypeLength(attribute.attributeType);
                }

                if (isDefined(this.selectedFeatures)) {
                    this.selectedFeatures.forEach(feature => {
                        if (isDefined(feature.attributes[attribute.prop]) && feature.attributes[attribute.prop] !== null) {
                            attribute.value = feature.attributes[attribute.prop];
                        }
                    });

                    this.attributes.push(attribute);
                } else {
                    this.attributes.push(attribute);
                }
            }
        });

        this.pristineAttributes = window.structuredClone(this.attributes);

        if (callback) {
            callback();
        }
    }
    getAttributes() {
        const attributes = {
            featuretype: 'general',
            featuresubtype: this.selectedAnnotationType.id,
            guid: this.planModelService.getGuid(),
            rotationdms: undefined,
            rotation: undefined
        };

        const attributesGroup = {};

        this.attributes.forEach(attribute => {
            attributes[attribute.prop] = attribute.value;
            attributesGroup[attribute.prop] = attribute.group;
        });

        if (isDefined(attributes.rotationdms)) {
            attributes.rotation = this.angleUtilService.dmsToDd(attributes.rotationdms);
        }
        return {
            attributes: attributes,
            attributesGroup: attributesGroup
        };
    }
    setupFeatureAttributes(callback) {
        console.debug(this.title + ' : setupFeatureAttributes');

        this.pristineAttributes = [];
        this.attributes = [];
        this.annotationTypes = [];

        this.lookupService.getLookups('annotationType').forEach(v => {
            v.value = v.description;
            this.annotationTypes.push(v);
        });

        this.selectedAnnotationType = this.annotationTypes[0];
        this.loadAnnotationTypeLookup(this.selectedAnnotationType, callback);
    }
    clearFeatureAttributes() {
        console.debug(`${this.title} : clearFeatureAttributes`);

        if (this.pristineAttributes && this.pristineAttributes.length) {
            this.attributes.splice(0, this.attributes.length, ...window.structuredClone(this.pristineAttributes));
        }
    }
    getAttributeTypeLength(attributeType) {
        console.debug(this.title + ' : getAttributeTypeLength');

        const lengthStartIndex = attributeType.indexOf('(') + 1;
        const lengthEndIndex = attributeType.indexOf(')');
        return attributeType.substring(lengthStartIndex, lengthEndIndex);
    }
    private updateToolServices() {
        console.debug(this.title + ' : updateToolServices');
        if (this.tool.type === 'edit') {
            this.annotationEditorToolService.setupFeatureEventModification(this.tool, this.attributes);
        }
        if (this.tool.type === 'construct') {
            this.annotationConstructionToolService.setupFeatureEventModification(() => {}, this.tool, this.attributes);
        }
    }

    hasError(fieldName) {
        const form = this.getForm();
        const field = form.controls[fieldName];
        return field && field.invalid && (field.dirty || form.submitted);
    }
    hasRequiredError(fieldName) {
        return this.hasError(fieldName) && this.getForm().controls[fieldName].errors['required'];
    }
    doCheck() {
        const form = this.getForm();
        if (!form) {
            return;
        }

        if (this.mapToolsService.isInactive('ANNOTATION_TOOLS_CREATE') && this.mapToolsService.isInactive('ANNOTATION_TOOLS_EDIT')) {
            // When create tool is deactivated, the form, parent form and empty required fields must be set back to a pristine state
            if (this.setFormPristineOnDeactivation && form.submitted) {
                this.setFormPristineOnDeactivation = false;
                this.setFormPristine();
            }
        } else if (!this.setFormPristineOnDeactivation) {
            this.setFormPristineOnDeactivation = true;
        }

        if (this.active) {
            if (this.mapToolsService.isActive('ANNOTATION_TOOLS_CREATE')) {
                // Create tool should stay active when annotation type is changed, but annotation creation should be disabled if the form is invalid
                if (form.invalid) {
                    if (!this.createActiveFormInvalid) {
                        this.createActiveFormInvalid = true;
                        this.eventBus.cast('constructFinishAnnotationTool');
                    }
                } else if (this.createActiveFormInvalid) {
                    // The form may be valid with an invalid pattern field due to the validation debounce time, so recheck the form validity
                    form.onSubmit(undefined);
                    if (form.valid) {
                        this.createActiveFormInvalid = false;
                        this.eventBus.cast('constructCreateAnnotationTool');
                    }
                }
            } else if (this.createActiveFormInvalid) {
                this.createActiveFormInvalid = false;
            }
        }

        if (this.tool.errors) {
            const minOptionalErrMsg = 'At least one of the fields must be completed!';
            if (this.hasMinOptionalError()) {
                if (!this.tool.errors.includes(minOptionalErrMsg)) {
                    this.tool.errors.push(minOptionalErrMsg);
                }
            } else {
                if (this.tool.errors.includes(minOptionalErrMsg)) {
                    this.tool.errors.splice(this.tool.errors.indexOf(minOptionalErrMsg), 1);
                }
            }
        }
    }
    isRequired(prop) {
        const attr = this.attributes.find(a => a.prop === prop);

        if (!attr || !attr.editable) {
            return false;
        }

        if (this.tool.type === 'edit') {
            const form = this.getForm();
            const field = form.controls[`${this.tool.type}${prop}`];
            return (!attr.group || attr.mandatory) && (attr.value || (field && field.dirty));
        }

        return attr.mandatory;
    }
    isDisabled(prop) {
        const attr = this.attributes.find(a => a.prop === prop);

        if (!attr || !attr.editable) {
            return true;
        }

        if (this.tool.type === 'edit') {
            const form = this.getForm();
            const field = form.controls[`${this.tool.type}${prop}`];
            return !attr.group && !attr.value && field && field.pristine;
        }

        return false;
    }

    ngOnDestroy() {
        console.info(`${this.title} - $onDestroy`);
        this.mapToolsService.deactivate('ANNOTATION_TOOLS');
    }

    changeSelectedAttribute(attribute: any, option: any) {
        attribute.value = option;
    }
}
